import { create } from 'zustand';
import { NavigateFunction } from 'react-router-dom';
import { isMobileOnly } from 'react-device-detect';
import { AxiosHeaders, HttpStatusCode } from 'axios';
import { createDI } from '../../../context/di/di';
import { ApiError, ApiResponse, useApi } from '../../../api/api';
import { useSessionStore } from '../../../context/session';
import { LoginReponse } from '../../../model/login_response';
import { AreasReponse } from '../../../model/areas_response';
import { Messages } from '../../../constant/messages';

/**
 * ログイン画面 状態を管理するための DI コンテキスト
 */
export type LoginState = {
  /**
   * アカウント情報
   */
  account: { username: string; password: string };

  /**
   * アカウント情報（パスワードリセット受付）
   */
  passResetAccountName: string;

  /**
   * エラーメッセージ
   */
  error: string;

  /**
   * エラーメッセージ（パスワードリセット受付）
   */
  passResetError: string;
};

/**
 * ログイン画面 アクションを管理するための DI コンテキスト(アクション)
 */
export type LoginAction = {
  /**
   * ユーザ名の設定
   * @param username
   */
  setAccountName: (username: string) => void;

  /**
   * パスワードの設定
   * @param password
   */
  setPassword: (password: string) => void;

  /**
   * アカウント名（パスワードリセット受付）
   * @param password
   */
  setPassResetAccountName: (passResetAccountName: string) => void;

  /**
   * エラーの設定
   * @param error
   */
  setError: (error: string) => void;

  /**
   * エラーの設定（パスワードリセット受付）
   * @param error
   */
  setPassResetError: (passResetError: string) => void;

  /**
   * ログイン
   * @param navigate
   */
  login: (navigate: NavigateFunction) => Promise<void>;

  /**
   * ログアウト
   * @param navigate
   */
  logout: (navigate: NavigateFunction) => Promise<void>;

  /**
   * パスワードリセット受付
   * @param navigate
   */
  reset: (navigate: NavigateFunction) => Promise<void>;
};

/**
 * ログイン画面 状態とアクションを管理するための DI コンテキスト
 */
export type LoginStore = LoginState & LoginAction; // ここで状態とアクションを結合する

/**
 * ログイン画面 状態とアクションを管理するための DI コンテキストを作成
 */
const [Provider, use] = createDI<LoginStore>();
export { Provider as LoginStoreProvider, use as useLoginStore };

/**
 * ログイン画面 状態とアクション
 */
export const defaultLoginStore = () => {
  const api = useApi();
  const onLogin = useSessionStore((selector) => selector.login);
  const setArea = useSessionStore((selector) => selector.setAreaInSession);
  const onLogout = useSessionStore((selector) => selector.logout);

  return create<LoginStore>((set, get) => {
    return {
      account: { username: '', password: '' },
      passResetAccountName: '',
      error: '',
      passResetError: '',
      setAccountName: (username: string) => {
        const { account } = get();
        set({ account: { ...account, username } });
      },
      setPassword: (password: string) => {
        const { account } = get();
        set({ account: { ...account, password } });
      },
      setPassResetAccountName: (passResetAccountName: string) => {
        set({ passResetAccountName });
      },
      setError: (error: string) => {
        set({ error });
      },
      setPassResetError: (passResetError: string) => {
        set({ passResetError });
      },
      login: async (navigate: NavigateFunction) => {
        const { account, setError } = get();
        try {
          setError('');
          const res = await api.auth('/api/auth', account.username, account.password).post();
          if (res.isError) {
            onLogin(undefined);
            if (res.status === 500) {
              window.alert(Messages.ERR_OTHER);
              return;
            }
            setError('アカウント名またはパスワードが異なります');
            return;
          }
          const header = res.headers as AxiosHeaders;
          const token = header.get('X-Token');
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          sessionStorage.setItem('token', token.toString());
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          window.session.token = token.toString();

          const resArea = await api.path('/api/resources/divisions').get();
          if (resArea.isError) {
            setError(Messages.ERR_INTERNAL_SERVER_ERROR);
            onLogin(undefined);
            sessionStorage.removeItem('token');
            return;
          }

          const areaData = resArea.data as AreasReponse;
          if (areaData.length === 0) {
            setError(Messages.ERR_INTERNAL_SERVER_ERROR);
            onLogin(undefined);
            sessionStorage.removeItem('token');
            return;
          }

          onLogin(res.data as LoginReponse);
          setArea(resArea.data as AreasReponse);

          const loginData = res.data as LoginReponse;
          if (isMobileOnly) {
            // スマホの場合、レスポンシブ対応しないメイン画面に遷移しないため権限にかかわらず通知一覧に遷移
            navigate('/notification');
          } else {
            navigate('/' + loginData.mainpage);
          }
        } catch (e) {
          onLogin(undefined);
          setArea(undefined);

          if (e instanceof ApiError) {
            if (e.response.status === -1) {
              window.alert(Messages.ERR_DIALOG_TIMEOUT);
            }
          }
        }
      },

      logout: async (navigate) => {

        await api.path('/api/auth').delete();

        onLogout();

        sessionStorage.removeItem('token');
        set({ account: { username: '', password: '' } });
        navigate('/'); // ログイン
      },

      reset: async (navigate: NavigateFunction) => {
        const { passResetAccountName } = get();
        const res = await api.path('/api/auth/reset').post(
          { accountId: `${passResetAccountName}` },
        );
        if (res.isError) {
          throw new ApiError(res);
        }
      },

    };
  });
};
